import { useCallback, useState } from 'react';

interface UseDragToDismissOptions {
  /**
   * Callback triggered when user drags past threshold
   */
  onDismiss: () => void;
  /**
   * Minimum drag distance in pixels to trigger dismiss
   * @default 100
   */
  threshold?: number;
  /**
   * Whether drag-to-dismiss is enabled
   * @default true
   */
  enabled?: boolean;
  /**
   * Direction of drag to dismiss
   * - 'down': drag down to dismiss (for bottom sheets)
   * - 'up': drag up to dismiss (for top sheets)
   * @default 'down'
   */
  direction?: 'down' | 'up';
}

interface DragHandlers {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseMove: (e: React.MouseEvent) => void;
  onMouseUp: () => void;
  onMouseLeave: () => void;
}

interface UseDragToDismissReturn {
  /**
   * Event handlers to attach to draggable element
   */
  dragHandlers: DragHandlers;
  /**
   * Inline styles for transform during drag
   */
  dragStyles: React.CSSProperties;
  /**
   * Whether element is currently being dragged
   */
  isDragging: boolean;
  /**
   * Current drag offset in pixels
   */
  dragOffset: number;
}

/**
 * Custom hook for implementing drag-to-dismiss gesture on bottom sheets/modals
 *
 * @example
 * const { dragHandlers, dragStyles, isDragging } = useDragToDismiss({
 *   onDismiss: handleClose,
 *   threshold: 100
 * });
 *
 * <div {...dragHandlers} style={dragStyles} className={isDragging ? 'dragging' : ''}>
 *   Content
 * </div>
 */
export const useDragToDismiss = ({
  onDismiss,
  threshold = 100,
  enabled = true,
  direction = 'down',
}: UseDragToDismissOptions): UseDragToDismissReturn => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);

  // Start drag gesture
  const handleDragStart = useCallback(
    (clientY: number) => {
      if (!enabled) return;

      setIsDragging(true);
      setDragStartY(clientY);
      setDragOffset(0);
    },
    [enabled]
  );

  // Update drag position
  const handleDragMove = useCallback(
    (clientY: number) => {
      if (!enabled || !isDragging) return;

      const delta = clientY - dragStartY;

      // Allow dragging in the correct direction based on sheet position
      if (direction === 'down') {
        // Bottom sheet: only allow dragging down (positive delta)
        if (delta > 0) {
          setDragOffset(delta);
        }
      } else {
        // Top sheet: only allow dragging up (negative delta)
        if (delta < 0) {
          setDragOffset(delta);
        }
      }
    },
    [enabled, isDragging, dragStartY, direction]
  );

  // End drag gesture and determine if should dismiss
  const handleDragEnd = useCallback(() => {
    if (!enabled || !isDragging) return;

    setIsDragging(false);

    // Check if dragged past threshold in the correct direction
    const isDismissed =
      direction === 'down'
        ? dragOffset > threshold // Bottom sheet: dragged down enough
        : Math.abs(dragOffset) > threshold; // Top sheet: dragged up enough

    if (isDismissed) {
      // Reset drag state BEFORE dismissing to avoid transform conflicts
      setDragOffset(0);
      // Small delay to ensure state is cleared before close animation
      setTimeout(() => {
        onDismiss();
      }, 10);
    } else {
      // Reset position with animation
      setDragOffset(0);
    }
  }, [enabled, isDragging, dragOffset, threshold, direction, onDismiss]);

  // Touch event handlers for mobile
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      handleDragStart(e.touches[0].clientY);
    },
    [handleDragStart]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      handleDragMove(e.touches[0].clientY);
    },
    [handleDragMove]
  );

  const handleTouchEnd = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  // Mouse event handlers for desktop testing
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      handleDragStart(e.clientY);
    },
    [handleDragStart]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      handleDragMove(e.clientY);
    },
    [handleDragMove]
  );

  const handleMouseUp = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  // Combine all handlers
  const dragHandlers: DragHandlers = {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onMouseUp: handleMouseUp,
    onMouseLeave: handleMouseUp, // Treat mouse leave as drag end
  };

  // Calculate transform style - show live feedback during drag
  const shouldApplyTransform = dragOffset !== 0 && isDragging;

  const dragStyles: React.CSSProperties = {
    transform: shouldApplyTransform ? `translateY(${dragOffset}px)` : undefined,
  };

  return {
    dragHandlers,
    dragStyles,
    isDragging,
    dragOffset,
  };
};
